import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Button, Card, CircularProgress, Divider, Stack, styled, SxProps, Typography } from "@mui/material";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { AdminAssetSingleDto, DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import MetadataListItem from "../../Misc/Metadata/MetadataListItem";
import { ModalContext } from "../../Modals";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import moment from "moment";
import TableCellAdamStatusHistory from "../../Table/TableCellAdamStatusHistory/table-cell-adam-status-history";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { ChangeStatusRequest } from "../../../Core/Api/Types/change-status-request";
import { assetService, cacheService } from "../../../Core/Api/services";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import { produce } from "immer";
import { DbStatusTransitionInfos } from "../../../Core/Api/Types/db-status-transition-infos";
import { useTranslation } from "react-i18next";

export interface AssetHeroAdamCardProps {
  sx?: SxProps;
  asset: AdminAssetSingleDto;
  setAssetData:
    | React.Dispatch<React.SetStateAction<AdminAssetSingleDto | undefined>>
    | React.Dispatch<React.SetStateAction<DbAssetObjectInfos | undefined>>;
}

const AssetHeroAdamCard: FC<AssetHeroAdamCardProps> = (props) => {
  const { t } = useTranslation();
  const { sx = {}, asset, setAssetData } = props;

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueCustomSnackbar = useCustomSnackbar();
  const [isWaiting, setIsWaiting] = useState<"" | "accept" | "reject">("");
  const [allowedTransitions, setAllowedTransitions] = useState<DbStatusTransitionInfos[]>([]);
  const idBaMeta = cacheService.GetMetadata().find((x) => x.tagName === "rc_ba_id");

  useEffect(() => {
    assetService.GetStatusTransitions(asset.id).then((response) => {
      if (response.success && response.transitions) {
        setAllowedTransitions(response.transitions);
      }
    });
  }, [asset.id]);

  const canValidate = useMemo(() => {
    if (asset.status === ObjectStatus.NeedCheckOrEditing) {
      return (
        allowedTransitions.find(
          (x) => x.inputStatus === ObjectStatus.NeedCheckOrEditing && x.outputStatuses?.includes(ObjectStatus.Accepted)
        ) !== undefined
      );
    } else if (asset.status === ObjectStatus.Accepted) {
      return (
        allowedTransitions.find(
          (x) =>
            x.inputStatus === ObjectStatus.Accepted && x.outputStatuses?.includes(ObjectStatus.AcceptedForBroadcast)
        ) !== undefined
      );
    }
  }, [asset.id, allowedTransitions]);

  const handleAcceptClick = () => {
    if (asset.status === ObjectStatus.NeedCheckOrEditing) {
      let request = new ChangeStatusRequest({ status: ObjectStatus.Accepted, message: "Accepted", objectId: asset.id });
      setIsWaiting("accept");
      assetService.ChangeStatus(request).then((response) => {
        setIsWaiting("");
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.statusChangedAndObjectAccepted"), "success");
          setAssetData(
            produce((draft) => {
              draft!.status = ObjectStatus.Accepted;
              draft!.statusHistory = [
                {
                  status: request.status,
                  comment: request.message,
                  date: moment().format(GlobalConst.MOMENT_DATE_FORMAT_FULL),
                  id: -500,
                  isNew: true,
                  isSystem: false,
                  userId: cacheService.GetUserInfo()?.id,
                  userName: cacheService.GetUserInfo()?.name,
                },
                ...draft.statusHistory,
              ];
            })
          );
        } else {
          enqueueCustomSnackbar(response.message ?? t("alert:error.errorWhileChangingStatus"), "error");
        }
      });
    } else if (asset.status === ObjectStatus.Accepted) {
      if (!openModal) return;

      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        displayComment: false,
        title: t("modal:title.acceptForBroadcast"),
        onConfirm: () => {
          let request = new ChangeStatusRequest({
            status: ObjectStatus.AcceptedForBroadcast,
            message: "Accepted",
            objectId: asset.id,
          });
          setIsWaiting("accept");
          assetService.ChangeStatus(request).then((response) => {
            setIsWaiting("");
            if (response.success) {
              setAssetData(
                produce((draft) => {
                  draft!.status = ObjectStatus.AcceptedForBroadcast;
                  draft!.statusHistory = [
                    {
                      status: request.status,
                      comment: request.message,
                      date: moment().format(GlobalConst.MOMENT_DATE_FORMAT_FULL),
                      id: -500,
                      isNew: true,
                      isSystem: false,
                      userId: cacheService.GetUserInfo()?.id,
                      userName: cacheService.GetUserInfo()?.name,
                    },
                    ...draft.statusHistory,
                  ];
                })
              );
              enqueueCustomSnackbar(t("alert:success.requestTakenIntoAccount"), "success");
            } else {
              enqueueCustomSnackbar(response.message ?? t("alert:error.errorWhileChangingStatus"), "error");
            }
          });
        },
      });
    }
  };

  const canReject = useMemo(() => {
    if (asset.status === ObjectStatus.NeedCheckOrEditing) {
      return (
        allowedTransitions.find(
          (x) => x.inputStatus === ObjectStatus.NeedCheckOrEditing && x.outputStatuses?.includes(ObjectStatus.Rejected)
        ) !== undefined
      );
    } else if (asset.status === ObjectStatus.Accepted) {
      return (
        allowedTransitions.find(
          (x) =>
            x.inputStatus === ObjectStatus.Accepted && x.outputStatuses?.includes(ObjectStatus.RejectedForBroadcast)
        ) !== undefined
      );
    }
  }, [asset.id, allowedTransitions]);

  const handleRejectClick = () => {
    if (asset.status === ObjectStatus.NeedCheckOrEditing) {
      if (!openModal) return;

      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        displayComment: true,
        commentLabel: t("modal:body.letUsMessageToExplainYourSeason"),
        title: t("modal:title.rejectQuestionMark"),
        onConfirm: (comment) => {
          let request = new ChangeStatusRequest({
            status: ObjectStatus.Rejected,
            message: comment,
            objectId: asset.id,
          });
          setIsWaiting("reject");
          assetService.ChangeStatus(request).then((response) => {
            setIsWaiting("");
            if (response.success) {
              enqueueCustomSnackbar(t("alert:success.statusChangedAndObjectRejected"), "success");
              setAssetData(
                produce((draft) => {
                  draft!.status = ObjectStatus.Rejected;
                  draft!.statusHistory = [
                    {
                      status: request.status,
                      comment: request.message,
                      date: moment().format(GlobalConst.MOMENT_DATE_FORMAT_FULL),
                      id: -500,
                      isNew: true,
                      isSystem: false,
                      userId: cacheService.GetUserInfo()?.id,
                      userName: cacheService.GetUserInfo()?.name,
                    },
                    ...draft.statusHistory,
                  ];
                })
              );
            } else {
              enqueueCustomSnackbar(response.message ?? t("alert:error.errorWhileChangingStatus"), "error");
            }
          });
        },
      });
    } else if (asset.status === ObjectStatus.Accepted) {
      if (!openModal) return;

      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        displayComment: true,
        commentLabel: t("modal:body.letUsMessageToExplainYourSeason"),
        title: t("modal:title.rejectQuestionMark"),
        onConfirm: (comment) => {
          let request = new ChangeStatusRequest({
            status: ObjectStatus.RejectedForBroadcast,
            message: comment,
            objectId: asset.id,
          });
          setIsWaiting("reject");
          assetService.ChangeStatus(request).then((response) => {
            setIsWaiting("");
            if (response.success) {
              enqueueCustomSnackbar(t("alert:success.statusChangedAndObjectRejectedForBroadcast"), "success");
              setAssetData(
                produce((draft) => {
                  draft!.status = ObjectStatus.RejectedForBroadcast;
                  draft!.statusHistory = [
                    {
                      status: request.status,
                      comment: request.message,
                      date: moment().format(GlobalConst.MOMENT_DATE_FORMAT_FULL),
                      id: -500,
                      isNew: true,
                      isSystem: false,
                      userId: cacheService.GetUserInfo()?.id,
                      userName: cacheService.GetUserInfo()?.name,
                    },
                    ...draft.statusHistory,
                  ];
                })
              );
            } else {
              enqueueCustomSnackbar(response.message ?? t("alert:error.errorWhileChangingStatus"), "error");
            }
          });
        },
      });
    }
  };

  return (
    <StyledCard variant="outlined" sx={sx}>
      <Stack direction="row" justifyContent="space-between">
        <MainContent justifyContent="space-between" spacing={2}>
          <div>
            <Typography fontWeight={600} variant="subtitle2">
              Statut autopromotion
            </Typography>
          </div>
          <Stack spacing={1}>
            <TableCellAdamStatusHistory data={{ history: asset.statusHistory, status: asset.status }} />
            <Stack pt={1}>
              {idBaMeta && (
                <MetadataListItem title="Id ba">
                  {asset.metadata?.find((x) => x.id === idBaMeta.id)?.value ?? t("common:unknowAllUpperCase")}
                </MetadataListItem>
              )}
              <MetadataListItem title={t("menu:title.lastStatusWas")}>
                {asset.statusHistory.length > 0
                  ? asset.statusHistory[asset.statusHistory.length - 1].status
                  : t("common:undefined")}
              </MetadataListItem>
              <MetadataListItem title={t("menu:title.statusChangedAt")}>
                {asset.statusHistory.length > 0
                  ? moment(asset.statusHistory[asset.statusHistory.length - 1].date).format(
                      GlobalConst.MOMENT_DATE_FORMAT_FULL
                    )
                  : t("common:undefined")}
              </MetadataListItem>
              {(asset.status === ObjectStatus.Rejected || asset.status === ObjectStatus.RejectedForBroadcast) && (
                <MetadataListItem title={t("common:comment")}>{true ? t("common:comment") : "-"}</MetadataListItem> //TODO: comment from where
              )}
            </Stack>
          </Stack>
        </MainContent>
        <Divider orientation="vertical" sx={{ mr: 5, ml: 5, height: "auto" }} />
        {UserHasAcl(AclTypes.aclObjectChangeStatus) &&
          (asset.status === ObjectStatus.Accepted || asset.status === ObjectStatus.NeedCheckOrEditing) && (
            <Actions>
              <Button
                disabled={isWaiting !== "" || !canValidate}
                sx={{ mb: 1 }}
                onClick={handleAcceptClick}
                size="small"
                variant={isWaiting === "accept" ? "outlined" : "contained"}
                color={isWaiting === "accept" ? "info" : "primary"}
                startIcon={isWaiting === "accept" ? <CircularProgress size={16} /> : <CheckCircleOutlineOutlinedIcon />}
              >
                {t("button:valider")} {asset.status === ObjectStatus.Accepted ? "PAD" : "PAV"}
              </Button>
              <Button
                disabled={isWaiting !== "" || !canReject}
                size="small"
                onClick={handleRejectClick}
                startIcon={isWaiting === "reject" ? <CircularProgress size={16} /> : <LockOutlinedIcon />}
                color={isWaiting === "reject" ? "info" : "error"}
              >
                {t("common:refuser")}
              </Button>
            </Actions>
          )}
      </Stack>
    </StyledCard>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  padding: `${theme.spacing(3)} ${theme.spacing(5)} ${theme.spacing(3)} ${theme.spacing(3)}`,
  background: "linear-gradient(180deg, rgba(252, 164, 100, 0.05) 0%, rgba(252, 164, 100, 0) 100%)",
}));

const MainContent = styled(Stack)(() => ({
  flex: "1 1 auto",
}));

const Actions = styled(Stack)(() => ({
  width: "240px",
  flex: "0 0 auto",
}));

export default AssetHeroAdamCard;
